// src/pages/admin/OrganizationUtilities.jsx
// Utility component for hospital organization admins to manage organization-specific tables
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  useTheme,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { 
  Build as BuildIcon, 
  CheckCircle, 
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterAlt as FilterAltIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import EditHospitalOrganizationDialog from '../../components/EditHospitalOrganizationDialog';
import { createMissingTables } from '../../services/subscriptionService';
import subscriptionHistoryService from '../../services/subscriptionHistoryService';
import { getSubscriptionPackages } from '../../services/subscriptionService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { confirmAsync } from '../../utils/appNotify';
import { getOrganizationId, getOrganizationName } from '../../utils/organizationUtils';
import { downloadStyledTableExcel } from '../../utils/patientTableExcelExport';
import PageShell from '../../components/PageShell';
import dayjs from 'dayjs';
import { USERMGMT_URL } from '../../utils/envConfig';
import { 
  getHospitalOrganizationById, 
  updateHospitalOrganizationStatus 
} from '../../services/hospitalOrganizationService';
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from '../../utils/pageLayoutStyles';
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FORM_BODY_SX,
  DENSE_FIELD_PROPS,
  CASH_PAYMENT_DIALOG_PAPER_SX,
  FIT_CONTENT_DIALOG_CONTENT_SX,
} from '../../utils/formModalStyles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const OrganizationUtilities = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Subscription History states
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [showHistoryFilters, setShowHistoryFilters] = useState(false);
  const [historyFilterAction, setHistoryFilterAction] = useState('');
  const [historyFilterStatus, setHistoryFilterStatus] = useState('');

  // Manage Subscription states
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [manageSubscriptionDialogOpen, setManageSubscriptionDialogOpen] = useState(false);
  const [subscriptionActionType, setSubscriptionActionType] = useState('RENEW'); // 'RENEW', 'UPGRADE', 'DOWNGRADE'
  const [formData, setFormData] = useState({
    packageType: 'MONTHLY',
    amount: '',
    paymentMethod: 'ONLINE',
    notes: '',
    subscriptionType: '',
  });
  const [packageDetails, setPackageDetails] = useState(null);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [loadingPackageDetails, setLoadingPackageDetails] = useState(false);

  // Cash payment authentication states
  const [showCashPaymentDialog, setShowCashPaymentDialog] = useState(false);
  const [showSuperAdminAuth, setShowSuperAdminAuth] = useState(false);
  const [cashPaymentDetails, setCashPaymentDetails] = useState({
    payerName: '',
    payerMobile: '',
    recipientName: '',
    recipientMobile: '',
    paymentComment: '',
    paymentProofName: '',
    paymentProofData: '',
  });
  const [superAdminCredentials, setSuperAdminCredentials] = useState({
    username: '',
    password: '',
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [cashPaymentAuthenticated, setCashPaymentAuthenticated] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // Store the action to execute after authentication

  // Organization status states (for super_admin)
  const [organizationStatus, setOrganizationStatus] = useState(null);
  const [organizationStatusLoading, setOrganizationStatusLoading] = useState(false);

  // Edit hospital organization dialog
  const [editHospitalDialogOpen, setEditHospitalDialogOpen] = useState(false);

  // Get organizationId from user
  const organizationId = getOrganizationId();

  // Check if user is super_admin
  const isSuperAdmin = user?.roleName === 'super_admin' || 
                       user?.roleName === 'superadmin' || 
                       (user?.roleName || '').toLowerCase() === 'super_admin';

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      // Load subscription history and subscription data when tab is selected
      loadSubscriptionHistory();
      loadSubscriptionData();
    } else if (newValue === 0 && isSuperAdmin && organizationId) {
      // Load organization status when Organization Utilities tab is selected (for super_admin)
      loadOrganizationStatus();
    }
  };

  // Load organization status (for super_admin)
  const loadOrganizationStatus = async () => {
    if (!organizationId || !isSuperAdmin) return;
    
    try {
      setOrganizationStatusLoading(true);
      const organization = await getHospitalOrganizationById(organizationId);
      
      // Check both isActive (boolean) and status (enum) fields
      const isActive = organization?.isActive !== false && 
                      organization?.isActive !== 0 && 
                      organization?.isActive !== "false" &&
                      organization?.status !== "INACTIVE" &&
                      organization?.status !== "inactive";
      
      setOrganizationStatus({
        isActive,
        status: organization?.status || (isActive ? 'ACTIVE' : 'INACTIVE'),
        organization,
      });
    } catch (err) {
      console.error('Error loading organization status:', err);
      toast.error('Failed to load organization status');
      setOrganizationStatus(null);
    } finally {
      setOrganizationStatusLoading(false);
    }
  };

  // Handle activate organization
  const handleActivateOrganization = async () => {
    if (!organizationId) {
      toast.error('Organization ID not found');
      return;
    }

    try {
      setOrganizationStatusLoading(true);
      
      // Update hospital organization status
      await updateHospitalOrganizationStatus(organizationId, true);
      
      // Update subscription history status to ACTIVE
      try {
        const history = await subscriptionHistoryService.getSubscriptionHistory(organizationId);
        const latestHistory = Array.isArray(history) && history.length > 0 ? history[0] : null;
        if (latestHistory?.id) {
          await subscriptionHistoryService.updateSubscriptionHistoryStatus(
            latestHistory.id,
            'ACTIVE'
          );
          console.log('Subscription history status set to ACTIVE');
        } else {
          console.warn('No subscription history found to update');
        }
      } catch (subscriptionError) {
        console.error('Error updating subscription history status:', subscriptionError);
        // Don't fail the activation if subscription history update fails - log warning
        toast.warning('Organization activated but subscription history status update failed. Please contact support.');
      }
      
      toast.success('Organization activated successfully');
      await loadOrganizationStatus(); // Reload status
    } catch (error) {
      console.error('Error activating organization:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to activate organization';
      toast.error(errorMessage);
    } finally {
      setOrganizationStatusLoading(false);
    }
  };

  // Handle deactivate organization
  const handleDeactivateOrganization = async () => {
    if (!organizationId) {
      toast.error('Organization ID not found');
      return;
    }

    // Confirm before deactivating
    const confirmed = await confirmAsync(
      'Are you sure you want to deactivate this organization? Users will not be able to log in until it is reactivated.',
      { title: 'Deactivate organization', tone: 'warning', confirmLabel: 'Deactivate' }
    );
    if (!confirmed) {
      return;
    }

    try {
      setOrganizationStatusLoading(true);
      
      // Update hospital organization status
      await updateHospitalOrganizationStatus(organizationId, false);
      
      // Update subscription history status to INACTIVE
      try {
        const history = await subscriptionHistoryService.getSubscriptionHistory(organizationId);
        const latestHistory = Array.isArray(history) && history.length > 0 ? history[0] : null;
        if (latestHistory?.id) {
          await subscriptionHistoryService.updateSubscriptionHistoryStatus(
            latestHistory.id,
            'INACTIVE'
          );
          console.log('Subscription history status set to INACTIVE');
        } else {
          console.warn('No subscription history found to update');
        }
      } catch (subscriptionError) {
        console.error('Error updating subscription history status:', subscriptionError);
        // Don't fail the deactivation if subscription history update fails - log warning
        toast.warning('Organization deactivated but subscription history status update failed. Please contact support.');
      }
      
      toast.success('Organization deactivated successfully');
      await loadOrganizationStatus(); // Reload status
    } catch (error) {
      console.error('Error deactivating organization:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to deactivate organization';
      toast.error(errorMessage);
    } finally {
      setOrganizationStatusLoading(false);
    }
  };

  // Organization Utilities functions
  const handleCreateMissingTables = async () => {
    if (!organizationId) {
      toast.error('Organization ID not found. Please log in as a hospital organization admin.');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await createMissingTables(organizationId);
      setResult(response);
      
      if (response.success) {
        toast.success(
          `Successfully created ${response.tablesCreatedCount || 0} missing tables. ` +
          `${response.tablesExistingCount || 0} tables already existed.`
        );
      } else {
        toast.warning('Table creation completed with some issues. Check details below.');
      }
    } catch (error) {
      console.error('Error creating missing tables:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to create missing tables: ${errorMessage}`);
      setResult({
        success: false,
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Subscription History functions
  const loadSubscriptionHistory = async () => {
    if (!organizationId) return;
    
    try {
      setHistoryLoading(true);
      const data = await subscriptionHistoryService.getSubscriptionHistory(organizationId);
      const historyArray = Array.isArray(data) ? data : [];
      setHistory(historyArray);
      setFilteredHistory(historyArray);
    } catch (err) {
      console.error('Error loading subscription history:', err);
      toast.error('Failed to load subscription history');
      setHistory([]);
      setFilteredHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load organization status on mount if super_admin and on Organization Utilities tab
  useEffect(() => {
    if (tabValue === 0 && isSuperAdmin && organizationId) {
      loadOrganizationStatus();
    }
  }, [tabValue, isSuperAdmin, organizationId]);

  useEffect(() => {
    if (tabValue === 1 && history.length > 0) {
      let filtered = [...history];
      if (historySearchQuery.trim()) {
        const q = historySearchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          const subscriptionTypeName = item.subscriptionType?.subType || item.subscriptionType || '';
          const actionType = item.actionType || '';
          const status = item.status || '';
          const packageType = item.packageType || '';
          const paymentMethod = item.paymentMethod || '';
          const paymentStatus = item.paymentStatus || '';
          const notes = item.notes || '';

          const searchableText = [
            subscriptionTypeName,
            actionType,
            status,
            packageType,
            paymentMethod,
            paymentStatus,
            notes,
            item.amount?.toString() || '',
          ]
            .filter(Boolean)
            .map((v) => String(v).toLowerCase());

          return searchableText.some((text) => text.includes(q));
        });
      }
      if (historyFilterAction) {
        filtered = filtered.filter(
          (item) => String(item.actionType || '').toUpperCase() === historyFilterAction
        );
      }
      if (historyFilterStatus) {
        filtered = filtered.filter(
          (item) => String(item.status || '').toUpperCase() === historyFilterStatus
        );
      }
      setFilteredHistory(filtered);
      setHistoryPage(0);
    }
  }, [historySearchQuery, historyFilterAction, historyFilterStatus, history, tabValue]);

  const handleHistoryPageChange = (event, newPage) => {
    setHistoryPage(newPage);
  };

  const handleHistoryRowsPerPageChange = (event) => {
    setHistoryRowsPerPage(+event.target.value);
    setHistoryPage(0);
  };

  const handleHistoryExport = async () => {
    const rows = filteredHistory || [];
    const headers = [
      'ID',
      'Action Type',
      'Subscription Type',
      'Package Type',
      'Amount',
      'Start Date',
      'End Date',
      'Payment Method',
      'Payment Status',
      'Status',
      'Created At',
      'Notes',
    ];

    const tableRows = rows.map((row) => {
      const subscriptionTypeName = row.subscriptionType?.subType || row.subscriptionType || '';
      const startDate = row.startDate
        ? (dayjs(row.startDate).isValid() ? dayjs(row.startDate).format('YYYY-MM-DD') : row.startDate)
        : '';
      const endDate = row.endDate
        ? (dayjs(row.endDate).isValid() ? dayjs(row.endDate).format('YYYY-MM-DD') : row.endDate)
        : '';
      const createdAt = row.createdAt
        ? (dayjs(row.createdAt).isValid() ? dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : row.createdAt)
        : '';

      return [
        row.id || '',
        row.actionType || '',
        subscriptionTypeName,
        row.packageType || '',
        row.amount || '',
        startDate,
        endDate,
        row.paymentMethod || '',
        row.paymentStatus || '',
        row.status || '',
        createdAt,
        row.notes || '',
      ];
    });

    await downloadStyledTableExcel({
      filename: `subscription_history_${dayjs().format('YYYY-MM-DD')}`,
      sheetName: 'Subscription History',
      title: 'Subscription History',
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return dateString;
      
      // Format date part
      const datePart = date.format('DD-MM-YYYY');
      
      // Format time in 12-hour format with AM/PM
      let hours = date.hour();
      const minutes = date.minute();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      
      // Format with leading zeros
      const hoursStr = hours.toString().padStart(2, '0');
      const minutesStr = minutes.toString().padStart(2, '0');
      
      return `${datePart} ${hoursStr}:${minutesStr} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'ONBOARD':
        return 'success';
      case 'RENEW':
        return 'info';
      case 'UPGRADE':
        return 'warning';
      case 'DOWNGRADE':
        return 'error';
      case 'CANCEL':
        return 'default';
      default:
        return 'default';
    }
  };

  // Manage Subscription functions
  const loadSubscriptionData = async () => {
    if (!organizationId) return;

    try {
      setSubscriptionLoading(true);
      const [subscription, types] = await Promise.all([
        subscriptionHistoryService.getActiveSubscription(organizationId),
        subscriptionHistoryService.getSubscriptionTypes(),
      ]);
      setActiveSubscription(subscription);
      setSubscriptionTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      toast.error('Failed to load subscription data');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const calculatePrice = (basePrice, pkgType) => {
    switch (pkgType) {
      case 'QUARTERLY':
        return basePrice * 3;
      case 'YEARLY':
        return basePrice * 12;
      case 'MONTHLY':
      default:
        return basePrice;
    }
  };

  const fetchRenewalPackageDetails = async () => {
    try {
      setLoadingPackageDetails(true);
      const currentTypeName = activeSubscription.subscriptionType?.subType || 
                              activeSubscription.subscriptionType || 
                              '';
      
      if (!currentTypeName) {
        toast.error('Unable to determine current subscription type');
        return;
      }

      const { packages } = await getSubscriptionPackages();
      const currentPackage = packages.find(
        (pkg) => pkg.name?.toUpperCase() === currentTypeName.toUpperCase() ||
                 pkg.id?.toLowerCase() === currentTypeName.toLowerCase()
      );

      if (currentPackage) {
        setPackageDetails(currentPackage);
        const basePrice = currentPackage.price || 0;
        // Use current package type from activeSubscription if formData.packageType is not set yet
        const packageTypeToUse = formData.packageType || activeSubscription?.packageType || 'MONTHLY';
        const calculated = calculatePrice(basePrice, packageTypeToUse);
        setCalculatedAmount(calculated);
        setFormData(prev => ({ 
          ...prev, 
          packageType: packageTypeToUse,
          amount: calculated.toString() 
        }));
      } else {
        toast.error('Package details not found');
      }
    } catch (err) {
      console.error('Error fetching package details:', err);
      toast.error('Failed to fetch package details');
    } finally {
      setLoadingPackageDetails(false);
    }
  };

  const fetchUpgradePackageDetails = async (subscriptionTypeName) => {
    try {
      setLoadingPackageDetails(true);
      if (!subscriptionTypeName) {
        setPackageDetails(null);
        setCalculatedAmount(0);
        setFormData(prev => ({ ...prev, amount: '' }));
        return;
      }

      const { packages } = await getSubscriptionPackages();
      const selectedPackage = packages.find(
        (pkg) => pkg.name?.toUpperCase() === subscriptionTypeName.toUpperCase() ||
                 pkg.id?.toLowerCase() === subscriptionTypeName.toLowerCase()
      );

      if (selectedPackage) {
        setPackageDetails(selectedPackage);
        const basePrice = selectedPackage.price || 0;
        const calculated = calculatePrice(basePrice, formData.packageType);
        setCalculatedAmount(calculated);
        setFormData(prev => ({ ...prev, amount: calculated.toString() }));
      } else {
        toast.error('Package details not found');
        setPackageDetails(null);
        setCalculatedAmount(0);
        setFormData(prev => ({ ...prev, amount: '' }));
      }
    } catch (err) {
      console.error('Error fetching package details:', err);
      toast.error('Failed to fetch package details');
    } finally {
      setLoadingPackageDetails(false);
    }
  };

  const handlePackageTypeChange = (newPackageType) => {
    if (packageDetails) {
      const basePrice = packageDetails.price || 0;
      const calculated = calculatePrice(basePrice, newPackageType);
      setCalculatedAmount(calculated);
      setFormData(prev => ({ ...prev, packageType: newPackageType, amount: calculated.toString() }));
    }
  };

  const handleSubscriptionTypeChange = (newSubscriptionType) => {
    setFormData(prev => ({ ...prev, subscriptionType: newSubscriptionType }));
    fetchUpgradePackageDetails(newSubscriptionType);
  };

  const handleCashPaymentDetailsSubmit = () => {
    // Validate cash payment details
    if (!cashPaymentDetails.payerName.trim()) {
      toast.error('Please enter payer name');
      return;
    }
    if (!cashPaymentDetails.payerMobile.trim() || cashPaymentDetails.payerMobile.length !== 10) {
      toast.error('Please enter valid payer mobile number (10 digits)');
      return;
    }
    if (!cashPaymentDetails.recipientName.trim()) {
      toast.error('Please enter recipient name');
      return;
    }
    if (!cashPaymentDetails.recipientMobile.trim() || cashPaymentDetails.recipientMobile.length !== 10) {
      toast.error('Please enter valid recipient mobile number (10 digits)');
      return;
    }

    // Close cash payment dialog and show super admin authentication
    setShowCashPaymentDialog(false);
    setShowSuperAdminAuth(true);
  };

  const handleSuperAdminAuth = async () => {
    if (!superAdminCredentials.username.trim() || !superAdminCredentials.password.trim()) {
      setAuthError('Please enter both username and password');
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      // Authenticate super admin using login API
      // Username is mobile number, password is the password
      const response = await fetch(`${USERMGMT_URL}/loginWithMobile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile: superAdminCredentials.username,
          password: superAdminCredentials.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || errorData.message || 'Authentication failed');
      }

      const { user } = await response.json();
      
      // Check if user is superadmin
      const role = (user?.roleName || user?.role || '').toLowerCase();
      if (role !== 'superadmin' && role !== 'super_admin') {
        throw new Error('Only super admin can authenticate cash payments');
      }

      // Authentication successful
      toast.success('Authentication successful');
      setCashPaymentAuthenticated(true);
      setShowSuperAdminAuth(false);
      
      // Execute the pending action
      if (pendingAction) {
        await pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      console.error('Super admin authentication error:', error);
      setAuthError(error.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setAuthLoading(false);
    }
  };

  const executeRenew = async () => {
    try {
      if (!calculatedAmount || calculatedAmount <= 0) {
        toast.error('Invalid amount calculated');
        return;
      }

      const renewData = {
        organizationId: organizationId,
        amount: calculatedAmount,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === 'CASH' ? 'COMPLETED' : undefined,
        notes: formData.notes,
      };

      // Don't include payer/recipient details in renewData - backend DTO doesn't accept them
      // We'll update them separately after creation

      const renewalResult = await subscriptionHistoryService.renewSubscription(renewData);

      // Update payer and recipient details for CASH payment method (after creation)
      if (formData.paymentMethod === 'CASH' && renewalResult?.id) {
        try {
          await subscriptionHistoryService.updateSubscriptionHistoryCashDetails(
            renewalResult.id,
            cashPaymentDetails
          );
        } catch (cashDetailsError) {
          console.error('Error updating subscription history cash details:', cashDetailsError);
          // Don't fail the renewal if cash details update fails - log warning
          toast.warning('Subscription renewed but cash payment details update failed. Please contact support.');
        }
      }

      // Update subscription history status and hospital organization status for online payment
      if (formData.paymentMethod === 'ONLINE') {
        try {
          if (renewalResult?.id) {
            await subscriptionHistoryService.updateSubscriptionHistoryStatus(
              renewalResult.id,
              'INACTIVE'
            );
          } else {
            // Fallback: get latest history if id not in response
            const history = await subscriptionHistoryService.getSubscriptionHistory(organizationId);
            const latestHistory = Array.isArray(history) && history.length > 0 ? history[0] : null;
            if (latestHistory?.id) {
              await subscriptionHistoryService.updateSubscriptionHistoryStatus(
                latestHistory.id,
                'INACTIVE'
              );
            }
          }
        } catch (statusError) {
          console.error('Error updating subscription history status:', statusError);
        }

        // Update hospital organization status to inactive for online payment
        try {
          await updateHospitalOrganizationStatus(organizationId, false);
          console.log('Hospital organization status set to inactive for ONLINE payment (renew)');
        } catch (orgStatusError) {
          console.error('Error updating hospital organization status:', orgStatusError);
          // Don't fail the renewal if organization status update fails - log warning
          toast.warning('Subscription renewed but organization status update failed. Please contact support.');
        }
      }

      toast.success('Subscription renewed successfully!');
      setManageSubscriptionDialogOpen(false);
      setFormData({
        packageType: 'MONTHLY',
        amount: '',
        paymentMethod: 'ONLINE',
        notes: '',
        subscriptionType: '',
      });
      setPackageDetails(null);
      setCalculatedAmount(0);
      setSubscriptionActionType('RENEW');
      setCashPaymentAuthenticated(false);
      setCashPaymentDetails({
payerName: '',
                payerMobile: '',
                recipientName: '',
                recipientMobile: '',
                paymentComment: '',
                paymentProofName: '',
                paymentProofData: '',
              });
      loadSubscriptionData();
      loadSubscriptionHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to renew subscription';
      toast.error(errorMessage);
    }
  };

  const handleRenew = async () => {
    // If payment method is CASH, check if authenticated
    if (formData.paymentMethod === 'CASH') {
      if (!cashPaymentAuthenticated) {
        // If not authenticated, set pending action and show super admin auth dialog
        // (Cash payment details dialog should have already been shown when CASH was selected)
        setPendingAction(executeRenew);
        if (!showCashPaymentDialog && !showSuperAdminAuth) {
          // If dialogs are closed but not authenticated, show super admin auth
          setShowSuperAdminAuth(true);
        }
        return;
      }
    }
    
    await executeRenew();
  };

  const executeUpgradeDowngrade = async (actionType) => {
    try {
      if (!formData.subscriptionType) {
        toast.error('Please select a subscription type');
        return;
      }

      if (!calculatedAmount || calculatedAmount <= 0) {
        toast.error('Invalid amount calculated');
        return;
      }

      const changeData = {
        organizationId: organizationId,
        newSubscriptionType: formData.subscriptionType,
        packageType: formData.packageType,
        amount: calculatedAmount,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === 'CASH' ? 'COMPLETED' : undefined,
        actionType: actionType,
        notes: formData.notes,
      };

      // Don't include payer/recipient details in changeData - backend DTO doesn't accept them
      // We'll update them separately after creation

      const changeResult = await subscriptionHistoryService.changeSubscription(changeData);

      // Update payer and recipient details for CASH payment method (after creation)
      if (formData.paymentMethod === 'CASH' && changeResult?.id) {
        try {
          await subscriptionHistoryService.updateSubscriptionHistoryCashDetails(
            changeResult.id,
            cashPaymentDetails
          );
        } catch (cashDetailsError) {
          console.error('Error updating subscription history cash details:', cashDetailsError);
          // Don't fail the change if cash details update fails - log warning
          toast.warning(`Subscription ${actionType.toLowerCase()}d but cash payment details update failed. Please contact support.`);
        }
      }

      // Update subscription history status and hospital organization status for online payment
      if (formData.paymentMethod === 'ONLINE') {
        try {
          if (changeResult?.id) {
            await subscriptionHistoryService.updateSubscriptionHistoryStatus(
              changeResult.id,
              'INACTIVE'
            );
          } else {
            // Fallback: get latest history if id not in response
            const history = await subscriptionHistoryService.getSubscriptionHistory(organizationId);
            const latestHistory = Array.isArray(history) && history.length > 0 ? history[0] : null;
            if (latestHistory?.id) {
              await subscriptionHistoryService.updateSubscriptionHistoryStatus(
                latestHistory.id,
                'INACTIVE'
              );
            }
          }
        } catch (statusError) {
          console.error('Error updating subscription history status:', statusError);
        }

        // Update hospital organization status to inactive for online payment
        try {
          await updateHospitalOrganizationStatus(organizationId, false);
          console.log(`Hospital organization status set to inactive for ONLINE payment (${actionType.toLowerCase()})`);
        } catch (orgStatusError) {
          console.error('Error updating hospital organization status:', orgStatusError);
          // Don't fail the change if organization status update fails - log warning
          toast.warning(`Subscription ${actionType.toLowerCase()}d but organization status update failed. Please contact support.`);
        }
      }

      toast.success(`Subscription ${actionType.toLowerCase()}d successfully!`);
      setManageSubscriptionDialogOpen(false);
      setFormData({
        packageType: 'MONTHLY',
        amount: '',
        paymentMethod: 'ONLINE',
        notes: '',
        subscriptionType: '',
      });
      setPackageDetails(null);
      setCalculatedAmount(0);
      setSubscriptionActionType('RENEW');
      setCashPaymentAuthenticated(false);
      setCashPaymentDetails({
payerName: '',
                payerMobile: '',
                recipientName: '',
                recipientMobile: '',
                paymentComment: '',
                paymentProofName: '',
                paymentProofData: '',
              });
      loadSubscriptionData();
      loadSubscriptionHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${actionType.toLowerCase()} subscription`;
      toast.error(errorMessage);
    }
  };

  const handleUpgradeDowngrade = async (actionType) => {
    // If payment method is CASH, check if authenticated
    if (formData.paymentMethod === 'CASH') {
      if (!cashPaymentAuthenticated) {
        // If not authenticated, set pending action and show super admin auth dialog
        // (Cash payment details dialog should have already been shown when CASH was selected)
        setPendingAction(() => executeUpgradeDowngrade(actionType));
        if (!showCashPaymentDialog && !showSuperAdminAuth) {
          // If dialogs are closed but not authenticated, show super admin auth
          setShowSuperAdminAuth(true);
        }
        return;
      }
    }
    
    await executeUpgradeDowngrade(actionType);
  };

  if (!organizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="warning">
            This utility is only available for hospital organization admins.
            Please log in with a hospital organization admin account.
          </Alert>
        </Paper>
      </Box>
    );
  }

  const currentTypeName = activeSubscription?.subscriptionType?.subType || 
                          activeSubscription?.subscriptionType || 
                          '';

  const availableUpgrades = subscriptionTypes.filter(
    (type) => {
      const typeName = type.subType || type.name || '';
      return typeName !== currentTypeName &&
        (typeName === 'PREMIUM' ||
          (typeName === 'INTERMEDIATE' && currentTypeName === 'BASIC'));
    }
  );

  const availableDowngrades = subscriptionTypes.filter(
    (type) => {
      const typeName = type.subType || type.name || '';
      return typeName !== currentTypeName &&
        (typeName === 'BASIC' ||
          (typeName === 'INTERMEDIATE' && currentTypeName === 'PREMIUM'));
    }
  );

  const subscriptionHistoryColumns = [
    { id: 'id', label: 'ID' },
    { id: 'actionType', label: 'Action Type' },
    { id: 'subscriptionType', label: 'Subscription Type' },
    { id: 'packageType', label: 'Package Type' },
    { id: 'amount', label: 'Amount' },
    { id: 'startDate', label: 'Start Date' },
    { id: 'endDate', label: 'End Date' },
    { id: 'paymentMethod', label: 'Payment Method' },
    { id: 'paymentStatus', label: 'Payment Status' },
    { id: 'status', label: 'Status' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'notes', label: 'Notes' },
  ];

  return (
    <PageShell title="ORGANIZATION">
        <Box sx={PAGE_TABS_BAR_SX}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            {...PAGE_TAB_LIST_PROPS}
            aria-label="organization utilities tabs"
          >
            <Tab label="Organization Utilities" />
            <Tab label="Manage Subscription" />
          </Tabs>
        </Box>

        {/* Tab Panel 1: Organization Utilities */}
        <TabPanel value={tabValue} index={0}>
          <Grid
            item
            xs="12"
            display={'flex'}
            justifyContent={'flex-end'}
            mb={'5px'}
          >
            <Box display={'flex'} alignItems={'center'} gap={1}>
              {/* Activate/Deactivate buttons (only for super_admin) */}
              {isSuperAdmin && organizationId && (
                <>
                  {organizationStatusLoading ? (
                    <CircularProgress size={24} />
                  ) : organizationStatus && !organizationStatus.isActive ? (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleActivateOrganization}
                      disabled={organizationStatusLoading}
                    >
                      Activate
                    </Button>
                  ) : organizationStatus && organizationStatus.isActive ? (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeactivateOrganization}
                      disabled={organizationStatusLoading}
                    >
                      Deactivate
                    </Button>
                  ) : null}
                </>
              )}
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <BuildIcon />}
                onClick={handleCreateMissingTables}
                disabled={loading}
              >
                {loading ? 'Creating Tables...' : 'Create Missing Tables'}
              </Button>
            </Box>
          </Grid>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Organization ID:</strong> {organizationId}
            </Typography>
            {isSuperAdmin && organizationStatus && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Organization Status:</strong>{' '}
                <Chip
                  label={organizationStatus.isActive ? 'ACTIVE' : 'INACTIVE'}
                  color={organizationStatus.isActive ? 'success' : 'error'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Use this utility to create any missing organization-specific database tables.
              This will only create tables that don't exist and won't affect existing tables or data.
            </Typography>
          </Alert>

          {result && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Result
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {result.success ? (
                  <Box>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="body1" color="success.main" fontWeight="bold">
                        Operation Completed Successfully
                      </Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tables Created:
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {result.tablesCreatedCount || 0}
                        </Typography>
                        {result.tablesCreated && result.tablesCreated.length > 0 && (
                          <Box sx={{ mt: 1, maxHeight: 150, overflow: 'auto' }}>
                            {result.tablesCreated.map((table, index) => (
                              <Typography key={index} variant="caption" display="block">
                                • {table}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tables Already Existing:
                        </Typography>
                        <Typography variant="h6" color="info.main">
                          {result.tablesExistingCount || 0}
                        </Typography>
                        {result.tablesExisting && result.tablesExisting.length > 0 && (
                          <Box sx={{ mt: 1, maxHeight: 150, overflow: 'auto' }}>
                            {result.tablesExisting.map((table, index) => (
                              <Typography key={index} variant="caption" display="block" color="text.secondary">
                                • {table}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Grid>

                      {result.tablesFailed && result.tablesFailed.length > 0 && (
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Failed Tables ({result.tablesFailedCount || 0}):
                            </Typography>
                            {result.tablesFailed.map((table, index) => (
                              <Typography key={index} variant="caption" display="block">
                                • {table}
                              </Typography>
                            ))}
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ) : (
                  <Box>
                    <Box display="flex" alignItems="center" mb={2}>
                      <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                      <Typography variant="body1" color="error.main" fontWeight="bold">
                        Operation Failed
                      </Typography>
                    </Box>
                    <Alert severity="error">
                      <Typography variant="body2">
                        {result.error || 'Unknown error occurred'}
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </TabPanel>

        {/* Tab Panel 2: Subscription History */}
        <TabPanel value={tabValue} index={1}>
          <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditHospitalDialogOpen(true)}
            >
              Edit Hospital
            </Button>
            {!subscriptionLoading && activeSubscription && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={TABLE_ADD_BUTTON_SX}
                onClick={() => {
                  const currentPackageType = activeSubscription?.packageType || 'MONTHLY';
                  setSubscriptionActionType('RENEW');
                  setManageSubscriptionDialogOpen(true);
                  setFormData({
                    packageType: currentPackageType,
                    amount: '',
                    paymentMethod: 'ONLINE',
                    notes: '',
                    subscriptionType: '',
                  });
                  setPackageDetails(null);
                  setCalculatedAmount(0);
                  setTimeout(() => {
                    if (activeSubscription) {
                      fetchRenewalPackageDetails();
                    }
                  }, 100);
                }}
              >
                Manage Subscription
              </Button>
            )}
          </Grid>

          <Paper sx={{ width: '100%' }}>
            <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
              <Box sx={TABLE_SEARCH_ROW_SX}>
                <TextField
                  placeholder="Search subscription history"
                  value={historySearchQuery}
                  onChange={(e) => setHistorySearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Tooltip title={showHistoryFilters ? 'Hide filters' : 'Show filters'}>
                  <IconButton
                    size="small"
                    sx={TABLE_ICON_BUTTON_SX}
                    onClick={() => setShowHistoryFilters((v) => !v)}
                  >
                    <FilterAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export to Excel">
                  <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleHistoryExport}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh subscription history">
                  <IconButton
                    size="small"
                    sx={TABLE_ICON_BUTTON_SX}
                    onClick={() => {
                      loadSubscriptionHistory();
                      loadSubscriptionData();
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {showHistoryFilters && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ mt: 1 }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      label="Action Type"
                      value={historyFilterAction}
                      onChange={(e) => setHistoryFilterAction(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="RENEW">Renew</MenuItem>
                      <MenuItem value="UPGRADE">Upgrade</MenuItem>
                      <MenuItem value="DOWNGRADE">Downgrade</MenuItem>
                      <MenuItem value="CREATE">Create</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={historyFilterStatus}
                      onChange={(e) => setHistoryFilterStatus(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="EXPIRED">Expired</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Box>

          <TableContainer sx={{ maxHeight: 550 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {subscriptionHistoryColumns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align || 'left'}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={subscriptionHistoryColumns.length} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={subscriptionHistoryColumns.length} align="center">
                      <Alert>No subscription history found</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory
                    .slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage)
                    .map((row) => {
                      const subscriptionTypeName = row.subscriptionType?.subType || row.subscriptionType || 'Unknown';
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                          <TableCell>{row.id || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.actionType || '-'}
                              color={getActionTypeColor(row.actionType)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{subscriptionTypeName}</TableCell>
                          <TableCell>{row.packageType || '-'}</TableCell>
                          <TableCell>{formatAmount(row.amount)}</TableCell>
                          <TableCell>
                            {row.startDate
                              ? (dayjs(row.startDate).isValid()
                                  ? dayjs(row.startDate).format('DD-MM-YYYY')
                                  : row.startDate)
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {row.endDate
                              ? (dayjs(row.endDate).isValid()
                                  ? dayjs(row.endDate).format('DD-MM-YYYY')
                                  : row.endDate)
                              : '-'}
                          </TableCell>
                          <TableCell>{row.paymentMethod || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.paymentStatus || '-'}
                              size="small"
                              color={row.paymentStatus === 'COMPLETED' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.status || '-'}
                              size="small"
                              color={row.status === 'ACTIVE' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.createdAt)}
                          </TableCell>
                          <TableCell>{row.notes || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredHistory.length}
            rowsPerPage={historyRowsPerPage}
            page={historyPage}
            onPageChange={handleHistoryPageChange}
            onRowsPerPageChange={handleHistoryRowsPerPageChange}
          />
          </Paper>

          <EditHospitalOrganizationDialog
            open={editHospitalDialogOpen}
            onClose={() => setEditHospitalDialogOpen(false)}
            organizationId={organizationId}
            onSaved={() => {
              if (isSuperAdmin) {
                loadOrganizationStatus();
              }
            }}
          />

          {/* Manage Subscription Dialog - Single dialog for Renew, Upgrade, and Downgrade */}
          <Dialog 
            open={manageSubscriptionDialogOpen} 
            onClose={() => setManageSubscriptionDialogOpen(false)} 
            maxWidth="md" 
            fullWidth
            sx={STANDARD_FORM_DIALOG_PAPER_SX}
          >
            <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
              Manage Subscription
            </DialogTitle>
            <Box sx={{ px: 0.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
              <Tabs 
                value={subscriptionActionType} 
                onChange={(e, newValue) => {
                  setSubscriptionActionType(newValue);
                  // Reset form when switching actions
                  if (newValue === 'RENEW') {
                    const currentPackageType = activeSubscription?.packageType || 'MONTHLY';
                    setFormData({
                      packageType: currentPackageType,
                      amount: '',
                      paymentMethod: 'ONLINE',
                      notes: '',
                      subscriptionType: '',
                    });
                    setPackageDetails(null);
                    setCalculatedAmount(0);
                    setTimeout(() => {
                      if (activeSubscription) {
                        fetchRenewalPackageDetails();
                      }
                    }, 100);
                  } else {
                    setFormData({
                      packageType: 'MONTHLY',
                      amount: '',
                      paymentMethod: 'ONLINE',
                      notes: '',
                      subscriptionType: '',
                    });
                    setPackageDetails(null);
                    setCalculatedAmount(0);
                  }
                  // Reset cash payment authentication when switching tabs
                  setCashPaymentAuthenticated(false);
                  setCashPaymentDetails({
                    payerName: '',
                    payerMobile: '',
                    recipientName: '',
                    recipientMobile: '',
                    paymentComment: '',
                    paymentProofName: '',
                    paymentProofData: '',
                  });
                }}
                textColor="secondary"
                indicatorColor="secondary"
                variant="fullWidth"
                sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0.5, fontSize: "0.8125rem" } }}
              >
                <Tab label="Renew" value="RENEW" />
                {availableUpgrades.length > 0 && (
                  <Tab label="Upgrade" value="UPGRADE" />
                )}
                {availableDowngrades.length > 0 && (
                  <Tab label="Downgrade" value="DOWNGRADE" />
                )}
              </Tabs>
            </Box>
            <DialogContent sx={DENSE_FORM_BODY_SX}>
              <Box sx={{ pt: 0.75 }}>
                {/* Renew Section */}
                {subscriptionActionType === 'RENEW' && (
                  <Grid container spacing={2}>
                    {activeSubscription && (() => {
                      const endDate = activeSubscription.endDate ? dayjs(activeSubscription.endDate) : null;
                      const isExpired = endDate ? dayjs().isAfter(endDate, 'day') : false;
                      const daysRemaining = endDate ? endDate.diff(dayjs(), 'day') : 0;
                      
                      return (
                        <Grid item xs={12}>
                          <Alert 
                            severity={isExpired ? "warning" : "info"} 
                            sx={{ mb: 0.5, py: 0.25, "& .MuiAlert-message": { py: 0.25 } }}
                          >
                            {isExpired ? (
                              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                Your subscription has expired. Renewing will start a new subscription period.
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                Your current subscription is active until {endDate ? endDate.format('DD-MM-YYYY') : 'N/A'}. 
                                Renewing will extend your subscription from the current end date by the selected package duration.
                                {daysRemaining > 0 && ` (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining)`}
                              </Typography>
                            )}
                          </Alert>
                        </Grid>
                      );
                    })()}
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>Package Type</InputLabel>
                        <Select
                          value={formData.packageType}
                          onChange={(e) => handlePackageTypeChange(e.target.value)}
                          label="Package Type"
                          disabled={loadingPackageDetails || !packageDetails}
                        >
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                          <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                          <MenuItem value="YEARLY">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {loadingPackageDetails ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                          <Typography variant="caption">Loading...</Typography>
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={calculatedAmount > 0 ? calculatedAmount.toFixed(2) : ''}
                          InputProps={{ readOnly: true }}
                          helperText={packageDetails ? `Base: ₹${packageDetails.price || 0}/mo` : 'Select package type'}
                          {...DENSE_FIELD_PROPS}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          value={formData.paymentMethod}
                          onChange={(e) => {
                            const newPaymentMethod = e.target.value;
                            setFormData({ ...formData, paymentMethod: newPaymentMethod });
                            
                            if (newPaymentMethod === 'CASH') {
                              // Open cash payment dialog immediately when CASH is selected
                              setShowCashPaymentDialog(true);
                            } else {
                              // Reset cash payment authentication when payment method changes to ONLINE
                              setCashPaymentAuthenticated(false);
                              setCashPaymentDetails({
                                payerName: '',
                                payerMobile: '',
                                recipientName: '',
                                recipientMobile: '',
                                paymentComment: '',
                                paymentProofName: '',
                                paymentProofData: '',
                              });
                            }
                          }}
                          label="Payment Method"
                        >
                          <MenuItem value="ONLINE">Online</MenuItem>
                          <MenuItem value="CASH">Cash</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Notes (Optional)"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        {...DENSE_FIELD_PROPS}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Upgrade/Downgrade Section */}
                {(subscriptionActionType === 'UPGRADE' || subscriptionActionType === 'DOWNGRADE') && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>New Subscription Type</InputLabel>
                        <Select
                          value={formData.subscriptionType}
                          onChange={(e) => handleSubscriptionTypeChange(e.target.value)}
                          label="New Subscription Type"
                          disabled={loadingPackageDetails}
                        >
                          <MenuItem value="">
                            <em>Select subscription type</em>
                          </MenuItem>
                          {(subscriptionActionType === 'UPGRADE' ? availableUpgrades : availableDowngrades).map((type) => {
                            const typeName = type.subType || type.name || '';
                            const price = type.packageAmount || type.price || 0;
                            return (
                              <MenuItem key={type.id || typeName} value={typeName}>
                                {typeName} - ₹{price}/month
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>Package Type</InputLabel>
                        <Select
                          value={formData.packageType}
                          onChange={(e) => handlePackageTypeChange(e.target.value)}
                          label="Package Type"
                          disabled={loadingPackageDetails || !packageDetails}
                        >
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                          <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                          <MenuItem value="YEARLY">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {loadingPackageDetails ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                          <Typography variant="caption">Loading...</Typography>
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={calculatedAmount > 0 ? calculatedAmount.toFixed(2) : ''}
                          InputProps={{ readOnly: true }}
                          helperText={packageDetails ? `Base: ₹${packageDetails.price || 0}/mo` : 'Select subscription type'}
                          {...DENSE_FIELD_PROPS}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          value={formData.paymentMethod}
                          onChange={(e) => {
                            const newPaymentMethod = e.target.value;
                            setFormData({ ...formData, paymentMethod: newPaymentMethod });
                            
                            if (newPaymentMethod === 'CASH') {
                              // Open cash payment dialog immediately when CASH is selected
                              setShowCashPaymentDialog(true);
                            } else {
                              // Reset cash payment authentication when payment method changes to ONLINE
                              setCashPaymentAuthenticated(false);
                              setCashPaymentDetails({
                                payerName: '',
                                payerMobile: '',
                                recipientName: '',
                                recipientMobile: '',
                                paymentComment: '',
                                paymentProofName: '',
                                paymentProofData: '',
                              });
                            }
                          }}
                          label="Payment Method"
                        >
                          <MenuItem value="ONLINE">Online</MenuItem>
                          <MenuItem value="CASH">Cash</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes (Optional)"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        {...DENSE_FIELD_PROPS}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
              <Button onClick={() => setManageSubscriptionDialogOpen(false)} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>Cancel</Button>
              {subscriptionActionType === 'RENEW' && (
                <Button onClick={handleRenew} variant="contained" color="primary" sx={FORM_SUBMIT_BUTTON_SX}>
                  Renew
                </Button>
              )}
              {subscriptionActionType === 'UPGRADE' && (
                <Button
                  onClick={() => handleUpgradeDowngrade('UPGRADE')}
                  variant="contained"
                  color="warning"
                  disabled={!formData.subscriptionType}
                  sx={FORM_SUBMIT_BUTTON_SX}
                >
                  Upgrade
                </Button>
              )}
              {subscriptionActionType === 'DOWNGRADE' && (
                <Button
                  onClick={() => handleUpgradeDowngrade('DOWNGRADE')}
                  variant="outlined"
                  color="error"
                  disabled={!formData.subscriptionType}
                  sx={FORM_SUBMIT_BUTTON_SX}
                >
                  Downgrade
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Cash Payment Details Dialog */}
          <Dialog
            open={showCashPaymentDialog}
            onClose={() => {
              setShowCashPaymentDialog(false);
              setPendingAction(null);
              // Revert payment method to ONLINE if user cancels
              setFormData(prev => ({ ...prev, paymentMethod: 'ONLINE' }));
              setCashPaymentDetails({
                payerName: '',
                payerMobile: '',
                recipientName: '',
                recipientMobile: '',
                paymentComment: '',
                paymentProofName: '',
                paymentProofData: '',
              });
            }}
            maxWidth="md"
            fullWidth
            sx={CASH_PAYMENT_DIALOG_PAPER_SX}
          >
            <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
              Cash Payment Details
            </DialogTitle>
            <DialogContent sx={FIT_CONTENT_DIALOG_CONTENT_SX}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                Please provide the details of who is paying and to whom for cash payment authentication.
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Payer Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Payer Name"
                    value={cashPaymentDetails.payerName}
                    onChange={(e) =>
                      setCashPaymentDetails({
                        ...cashPaymentDetails,
                        payerName: e.target.value,
                      })
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Payer Mobile Number"
                    value={cashPaymentDetails.payerMobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCashPaymentDetails({
                        ...cashPaymentDetails,
                        payerMobile: value,
                      });
                    }}
                    inputProps={{ maxLength: 10 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                    Recipient Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Recipient Name"
                    value={cashPaymentDetails.recipientName}
                    onChange={(e) =>
                      setCashPaymentDetails({
                        ...cashPaymentDetails,
                        recipientName: e.target.value,
                      })
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Recipient Mobile Number"
                    value={cashPaymentDetails.recipientMobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCashPaymentDetails({
                        ...cashPaymentDetails,
                        recipientMobile: value,
                      });
                    }}
                    inputProps={{ maxLength: 10 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                    Comment / Proof of Payment
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    Optional — e.g. full cash, or half cash + half online. Upload a transaction screenshot or any proof.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Payment Comment / Notes"
                    placeholder="Example: Paid half cash and remaining amount online"
                    value={cashPaymentDetails.paymentComment || ''}
                    onChange={(e) =>
                      setCashPaymentDetails({
                        ...cashPaymentDetails,
                        paymentComment: e.target.value,
                      })
                    }
                  />
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload Proof
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Proof file must be under 5MB');
                            event.target.value = '';
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => {
                            setCashPaymentDetails((prev) => ({
                              ...prev,
                              paymentProofName: file.name,
                              paymentProofData: typeof reader.result === 'string' ? reader.result : '',
                            }));
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                    </Button>
                    {cashPaymentDetails.paymentProofName ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {cashPaymentDetails.paymentProofName}
                        <Button
                          size="small"
                          onClick={() =>
                            setCashPaymentDetails({
                              ...cashPaymentDetails,
                              paymentProofName: '',
                              paymentProofData: '',
                            })
                          }
                          sx={{ textTransform: 'none', ml: 0.5, minWidth: 'auto', p: 0 }}
                        >
                          Remove
                        </Button>
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Image or PDF (max 5MB)
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
              <Button
                onClick={() => {
                  setShowCashPaymentDialog(false);
                  setPendingAction(null);
                  // Revert payment method to ONLINE if user cancels
                  setFormData(prev => ({ ...prev, paymentMethod: 'ONLINE' }));
                  setCashPaymentDetails({
                    payerName: '',
                    payerMobile: '',
                    recipientName: '',
                    recipientMobile: '',
                    paymentComment: '',
                    paymentProofName: '',
                    paymentProofData: '',
                  });
                }}
                variant="outlined"
                color="primary"
                sx={FORM_CANCEL_BUTTON_SX}
              >
                Cancel
              </Button>
              <Button onClick={handleCashPaymentDetailsSubmit} variant="contained" color="primary" sx={FORM_SUBMIT_BUTTON_SX}>
                Continue
              </Button>
            </DialogActions>
          </Dialog>

          {/* Super Admin Authentication Dialog */}
          <Dialog
            open={showSuperAdminAuth}
            onClose={() => {
              setShowSuperAdminAuth(false);
              setShowCashPaymentDialog(true);
            }}
            maxWidth="md"
            fullWidth
            sx={STANDARD_FORM_DIALOG_PAPER_SX}
          >
            <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Super Admin Authentication</DialogTitle>
            <DialogContent sx={DENSE_FORM_BODY_SX}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Please authenticate with super admin credentials to proceed with cash payment.
              </Typography>
              {authError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {authError}
                </Alert>
              )}
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username (Mobile Number)"
                    value={superAdminCredentials.username}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setSuperAdminCredentials({
                        ...superAdminCredentials,
                        username: value,
                      });
                      setAuthError('');
                    }}
                    inputProps={{ maxLength: 10 }}
                    required
                    helperText="Enter super admin mobile number"
                    {...DENSE_FIELD_PROPS}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={superAdminCredentials.password}
                    onChange={(e) => {
                      setSuperAdminCredentials({
                        ...superAdminCredentials,
                        password: e.target.value,
                      });
                      setAuthError('');
                    }}
                    required
                    helperText="Enter super admin password"
                    {...DENSE_FIELD_PROPS}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
              <Button
                onClick={() => {
                  setShowSuperAdminAuth(false);
                  setShowCashPaymentDialog(true);
                }}
                disabled={authLoading}
                variant="outlined"
                color="primary"
                sx={FORM_CANCEL_BUTTON_SX}
              >
                Back
              </Button>
              <Button
                onClick={handleSuperAdminAuth}
                variant="contained"
                color="primary"
                disabled={authLoading}
                sx={FORM_SUBMIT_BUTTON_SX}
              >
                {authLoading ? <CircularProgress size={24} /> : 'Authenticate'}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>
    </PageShell>
  );
};

export default OrganizationUtilities;
